import { useEffect, useRef, useState } from 'react'

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export interface UseCountUpOptions {
  /** Animation length in ms. Default 800. */
  duration?: number
  /** Value to animate from on the very first render. Default 0. */
  from?: number
  /** Easing function mapping progress 0..1 to eased 0..1. Default easeOutCubic. */
  easing?: (t: number) => number
  /** Skip the animation entirely and just return the target. Default false. */
  disabled?: boolean
}

/**
 * Animates a number towards `target`, re-animating from wherever it currently is whenever
 * `target` changes. Returns the current in-between value — the caller decides how to format it
 * (integers, decimals, currency, …), so this hook is not tied to any feature.
 *
 * Honours `prefers-reduced-motion` (jumps straight to the value) and cancels its frame on
 * unmount or when the target changes.
 */
export const useCountUp = (target: number, options: UseCountUpOptions = {}): number => {
  const { duration = 500, from = 0, easing = easeOutCubic, disabled = false } = options

  const startFrom = disabled ? target : from
  const [value, setValue] = useState(startFrom)
  const currentRef = useRef(startFrom)
  const frameRef = useRef<number>()

  useEffect(() => {
    if (disabled || duration <= 0 || prefersReducedMotion()) {
      currentRef.current = target
      setValue(target)
      return undefined
    }

    const start = currentRef.current
    const delta = target - start
    if (delta === 0) return undefined

    let startTs: number | undefined
    const tick = (ts: number) => {
      if (startTs === undefined) startTs = ts
      const progress = Math.min((ts - startTs) / duration, 1)
      const next = progress >= 1 ? target : start + delta * easing(progress)
      currentRef.current = next
      setValue(next)
      if (progress < 1) frameRef.current = requestAnimationFrame(tick)
    }
    frameRef.current = requestAnimationFrame(tick)

    return () => {
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current)
    }
    // `easing` is expected to be a stable module-level function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration, disabled])

  return value
}
